import { checklistClient } from '../../config/api';
import {
  Group,
  GroupApiResponse,
  GroupsApiResponse,
  AddGroupRequest,
  UpdateGroupRequest,
} from '../types/checklist.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class GroupService {
  private readonly basePath = '/api/checklists';

  getAllGroups = async (checklistId: string): Promise<Group[]> => {
    try {
      const response = await checklistClient.get<GroupsApiResponse>(
        `${this.basePath}/${checklistId}/groups`
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching groups');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching groups');
    }
  };

  getGroupById = async (checklistId: string, groupId: string): Promise<Group> => {
    try {
      const response = await checklistClient.get<GroupApiResponse>(
        `${this.basePath}/${checklistId}/groups/${groupId}`
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching group');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching group');
    }
  };

  addGroup = async (checklistId: string, data: AddGroupRequest): Promise<Group> => {
    try {
      const response = await checklistClient.post<GroupApiResponse>(
        `${this.basePath}/${checklistId}/groups`,
        data
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error adding group');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error adding group');
    }
  };

  updateGroup = async (
    checklistId: string,
    groupId: string,
    data: UpdateGroupRequest
  ): Promise<Group> => {
    try {
      const response = await checklistClient.put<GroupApiResponse>(
        `${this.basePath}/${checklistId}/groups/${groupId}`,
        data
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error updating group');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating group');
    }
  };

  deleteGroup = async (checklistId: string, groupId: string): Promise<void> => {
    try {
      const response = await checklistClient.delete<GroupApiResponse>(
        `${this.basePath}/${checklistId}/groups/${groupId}`
      );

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting group');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting group');
    }
  };
}

export const groupService = new GroupService();
